import React, { useEffect, useState } from 'react';
import { axiosInstance } from '../App';
import { History, Clock, User, Shield, Settings as SettingsIcon } from 'lucide-react';

const ACTION_META = {
  'owner:complete_profile': { label: 'Profile completed', color: 'bg-blue-100 text-blue-700', icon: User },
  'owner:update_profile': { label: 'Profile updated', color: 'bg-blue-100 text-blue-700', icon: User },
  'admin:approve': { label: 'Approved', color: 'bg-green-100 text-green-700', icon: Shield },
  'admin:reject': { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: Shield },
  'admin:convert_type': { label: 'Type converted', color: 'bg-purple-100 text-purple-700', icon: Shield },
  'admin:outreach_update': { label: 'Outreach update', color: 'bg-amber-100 text-amber-700', icon: Shield },
  'system:unclaim': { label: 'Unclaimed', color: 'bg-gray-100 text-gray-700', icon: SettingsIcon },
};

const formatValue = (v) => {
  if (v === null || v === undefined || v === '') return <span className="text-gray-400 italic">empty</span>;
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) return v.length === 0 ? <span className="text-gray-400 italic">empty</span> : v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  const s = String(v);
  return s.length > 140 ? s.slice(0, 140) + '…' : s;
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const BusinessHistory = ({ businessId }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get(`/admin/business/${businessId}/history`);
        if (!cancelled) setEntries(res.data?.entries || []);
      } catch (e) {
        if (!cancelled) setError('Failed to load history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessId]);

  return (
    <div className="bg-gray-50 rounded-lg p-4" data-testid="business-history">
      <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
        <History className="h-5 w-5 text-purple-600" />
        Edit History
        {entries.length > 0 && <span className="text-sm font-normal text-gray-500">({entries.length})</span>}
      </h2>

      {loading && <p className="text-sm text-gray-500">Loading…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
      {!loading && !error && entries.length === 0 && (
        <p className="text-sm text-gray-500">No edit history recorded yet.</p>
      )}

      <ol className="space-y-3">
        {entries.map((entry) => {
          const meta = ACTION_META[entry.action] || { label: entry.action, color: 'bg-gray-100 text-gray-700', icon: SettingsIcon };
          const Icon = meta.icon;
          return (
            <li key={entry.id} className="bg-white rounded-lg border border-gray-200 p-3" data-testid={`history-entry-${entry.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${meta.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-[11px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${meta.color}`}>
                      {meta.label}
                    </span>
                    <span className="text-xs text-gray-500">
                      {entry.actor?.name || entry.actor?.email || entry.actor?.type}
                      {entry.actor?.type && ` · ${entry.actor.type}`}
                    </span>
                    <span className="text-xs text-gray-400 ml-auto flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatWhen(entry.createdAt)}
                    </span>
                  </div>
                  {entry.changes && entry.changes.length > 0 ? (
                    <table className="w-full text-xs mt-1">
                      <thead>
                        <tr className="text-gray-400 text-left">
                          <th className="font-normal pr-2 pb-1">Field</th>
                          <th className="font-normal pr-2 pb-1">Before</th>
                          <th className="font-normal pb-1">After</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {entry.changes.map((c, idx) => (
                          <tr key={idx}>
                            <td className="pr-2 py-1 font-mono text-gray-700 align-top">{c.field}</td>
                            <td className="pr-2 py-1 text-gray-500 align-top break-words">{formatValue(c.before)}</td>
                            <td className="py-1 text-gray-900 align-top break-words">{formatValue(c.after)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">No field-level changes recorded.</p>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
};

export default BusinessHistory;
